import React, { useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A', red: '#DC2626',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280', textDark: '#1F2937',
};

const OPERATIONS = [
  { key: 'addition', label: 'Addition', timeLimit: 60 },
  { key: 'subtraction', label: 'Subtraction', timeLimit: 60 },
  { key: 'multiplication', label: 'Multiplication', timeLimit: 120 },
  { key: 'division', label: 'Division', timeLimit: 120 },
];

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1120px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1120px', margin: '0 auto', padding: '20px' },
  card: { backgroundColor: COLORS.white, borderRadius: '9px', boxShadow: '0 3px 8px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '700', color: COLORS.textDark, marginBottom: '5px' },
  input: { width: '100%', padding: '9px 10px', border: `1px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: COLORS.white },
  select: { width: '100%', padding: '9px 10px', border: `1px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '14px', backgroundColor: COLORS.white, boxSizing: 'border-box' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '11px 20px', borderRadius: '6px', fontWeight: '700', border: 'none', cursor: 'pointer', fontSize: '15px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '10px 12px', borderRadius: '6px', marginBottom: '14px' },
};

const normaliseRow = (item) => ({
  item_number: item.item_number,
  item_code: item.item_code,
  expression: item.expression,
  correct_answer: item.correct_answer,
  status: 'Correct',
  student_response: '',
});

export default function NumeracyScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [stage, setStage] = useState('setup');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState('addition');
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const timepoint = initialTimepoint || 'Baseline';
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const [administrationBatchId, setAdministrationBatchId] = useState('');
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [responses, setResponses] = useState([]);
  const [notes, setNotes] = useState('');
  const [scoring, setScoring] = useState(null);
  const [lockedOperations, setLockedOperations] = useState([]);

  const refreshLockedOperations = React.useCallback(() => fetchWithTimeout(`${apiUrl}/api/numeracy/status/${student.student_code}`)
    .then((r) => r.json())
    .then((d) => setLockedOperations(d.locked || []))
    .catch(() => {}), [apiUrl, student.student_code]);

  React.useEffect(() => { refreshLockedOperations(); }, [refreshLockedOperations]);
  React.useEffect(() => { if (stage === 'setup') refreshLockedOperations(); }, [stage, refreshLockedOperations]);

  const opMeta = OPERATIONS.find((o) => o.key === operation);
  const isOperationLocked = (op) => lockedOperations.some((l) => l.operation_label === op.label && l.timepoint === timepoint);
  const isLockedForSelection = isOperationLocked(opMeta);
  const allOperationsComplete = OPERATIONS.every((op) =>
    isOperationLocked(op) || (stage === 'complete' && op.key === operation)
  );

  const counts = useMemo(() => {
    const correct = responses.filter((r) => r.status === 'Correct').length;
    const incorrect = responses.filter((r) => r.status === 'Incorrect').length;
    const unattempted = responses.filter((r) => r.status === 'Unattempted').length;
    return { correct, incorrect, unattempted, attempted: correct + incorrect, total: responses.length };
  }, [responses]);

  const startAdministration = async () => {
    if (isLockedForSelection) { setError(`${opMeta.label} has already been completed for ${timepoint}.`); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/numeracy/items/${operation}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load this test');
      const loaded = (data.items || []).map(normaliseRow);
      if (!loaded.length) throw new Error('No numeracy items were returned.');
      setResponses(loaded);
      setStage('administering');
    } catch (e) { setError(e.message || 'Could not start the administration'); }
    finally { setLoading(false); }
  };

  const updateStatus = (itemNumber, status) => {
    setResponses((prev) => prev.map((r) => r.item_number === itemNumber
      ? { ...r, status, student_response: status === 'Incorrect' ? r.student_response : '' }
      : r));
  };

  const updateIncorrectResponse = (itemNumber, value) => {
    setResponses((prev) => prev.map((r) => r.item_number === itemNumber ? { ...r, student_response: value } : r));
  };

  const goToReview = async () => {
    const missing = responses.filter((r) => r.status === 'Incorrect' && !String(r.student_response || '').trim());
    if (missing.length) {
      setError(`Enter the student's actual response for incorrect item${missing.length === 1 ? '' : 's'} ${missing.map((x) => x.item_number).join(', ')}.`);
      return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/numeracy/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_code: student.student_code, operation, screening_date: screeningDate, responses }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not score this administration');
      setScoring(data.scoring); setStage('review');
    } catch (e) { setError(e.message || 'Could not score this administration'); }
    finally { setLoading(false); }
  };

  const submitAndLock = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/numeracy/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code, operation, screening_date: screeningDate, timepoint,
          school_id: schoolId || student?.school_id || '', school_name: schoolName,
          programme_id: programmeId || student?.programme_id || '', programme_name: programmeName,
          assessor_name: assessorName, administration_mode: administrationMode,
          administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId, entered_by_name: facilitatorName, notes, responses,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this administration');
      setScoring(data.scoring || scoring); setStage('complete'); if (onSaved) onSaved(data);
    } catch (e) { setError(e.message || 'Could not save this administration'); }
    finally { setLoading(false); }
  };

  const Header = ({ title }) => <div style={styles.header}><div style={styles.headerContent}>
    <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
    <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '700' }}>{title}</span>
  </div></div>;

  const OperationStatus = () => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: '8px', marginBottom: '16px' }}>
    {OPERATIONS.map((op) => {
      const done = isOperationLocked(op);
      return <div key={op.key} style={{ border: `1px solid ${done ? '#86EFAC' : COLORS.borderGray}`, borderRadius: '7px', padding: '9px 10px', backgroundColor: done ? '#F0FDF4' : '#F8FAFC', fontSize: '13px', fontWeight: '700', color: done ? '#166534' : COLORS.textGray, textAlign: 'center' }}>
        {done ? '✓ ' : '○ '}{op.label}
      </div>;
    })}
  </div>;

  if (stage === 'setup') return <div style={styles.container}><Header title="Numeracy" /><div style={styles.body}>
    <div style={styles.card}>
      <h1 style={{ fontSize: '21px', color: COLORS.navyBlue, margin: '0 0 4px' }}>Numeracy Fluency - {student.full_name}</h1>
      <div style={{ fontSize: '12px', color: COLORS.textGray, marginBottom: '14px' }}>Timepoint: {timepoint}</div>
      <OperationStatus />
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: '10px', alignItems: 'end' }}>
        <div><label style={styles.label}>Operation</label><select style={styles.select} value={operation} onChange={(e) => setOperation(e.target.value)}>{OPERATIONS.map((o) => <option key={o.key} value={o.key}>{o.label} ({o.timeLimit}s)</option>)}</select></div>
        <div><label style={styles.label}>Date</label><input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} /></div>
        <div><label style={styles.label}>Mode</label><select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}><option value="Individual">Individual</option><option value="Group">Group</option></select></div>
        <div><label style={styles.label}>Assessor</label><input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} /></div>
      </div>
      {administrationMode === 'Group' && <div style={{ marginTop: '10px', maxWidth: '320px' }}><label style={styles.label}>Batch ID</label><input style={styles.input} value={administrationBatchId} onChange={(e) => setAdministrationBatchId(e.target.value)} /></div>}
      <div style={{ marginTop: '16px' }}><button style={{ ...styles.button, opacity: loading || isLockedForSelection ? 0.6 : 1 }} onClick={startAdministration} disabled={loading || isLockedForSelection}>{loading ? 'Loading…' : `Enter ${opMeta.label} results`}</button></div>
    </div>
  </div></div>;

  if (stage === 'administering') return <div style={styles.container}><Header title={`${opMeta.label}: enter results`} /><div style={styles.body}>
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <div><h1 style={{ fontSize: '20px', color: COLORS.navyBlue, margin: 0 }}>{opMeta.label}</h1><div style={{ fontSize: '12px', color: COLORS.textGray }}>{student.full_name} · {screeningDate} · {opMeta.timeLimit}s paper test</div></div>
        <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          <span style={{ padding: '6px 9px', borderRadius: '999px', background: '#F0FDF4', color: '#166534', fontWeight: '700', fontSize: '12px' }}>{counts.correct} correct</span>
          <span style={{ padding: '6px 9px', borderRadius: '999px', background: '#FEF2F2', color: '#991B1B', fontWeight: '700', fontSize: '12px' }}>{counts.incorrect} incorrect</span>
          <span style={{ padding: '6px 9px', borderRadius: '999px', background: '#F3F4F6', color: '#4B5563', fontWeight: '700', fontSize: '12px' }}>{counts.unattempted} unattempted</span>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ fontSize: '12px', color: COLORS.textGray, marginBottom: '12px' }}>Items follow the paper sheet in rows of three: 1, 2, 3 on the first row; 4, 5, 6 on the next. Correct is the default, so only change errors or items not attempted.</div>
      <div style={{ overflowX: 'auto', paddingBottom: '2px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(250px, 1fr))', gap: '8px', minWidth: '790px' }}>
          {responses.map((row) => <div key={row.item_number} style={{ border: `1px solid ${row.status === 'Incorrect' ? '#F59E0B' : COLORS.borderGray}`, borderRadius: '7px', padding: '8px 9px', backgroundColor: row.status === 'Unattempted' ? '#F9FAFB' : COLORS.white }}>
            <div style={{ display: 'grid', gridTemplateColumns: '28px minmax(78px,1fr) 58px 108px', gap: '6px', alignItems: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.textGray }}>{row.item_number}.</div>
              <div style={{ fontWeight: '800', color: COLORS.navyBlue, whiteSpace: 'nowrap' }}>{row.expression}</div>
              <div style={{ color: '#9CA3AF', fontWeight: '700', textAlign: 'center', whiteSpace: 'nowrap' }}>= {row.correct_answer}</div>
              <select style={{ ...styles.select, padding: '5px 6px', fontSize: '11px' }} value={row.status} onChange={(e) => updateStatus(row.item_number, e.target.value)}>
                <option value="Correct">Correct</option><option value="Incorrect">Incorrect</option><option value="Unattempted">Not attempted</option>
              </select>
            </div>
            {row.status === 'Incorrect' && <input style={{ ...styles.input, marginTop: '6px', padding: '6px 8px', fontSize: '12px', borderColor: row.student_response.trim() ? COLORS.borderGray : '#F59E0B' }} value={row.student_response} onChange={(e) => updateIncorrectResponse(row.item_number, e.target.value)} placeholder="Student's incorrect response" />}
          </div>)}
        </div>
      </div>
      <div style={{ marginTop: '16px' }}><button style={styles.button} onClick={goToReview} disabled={loading}>{loading ? 'Checking…' : 'Review result'}</button></div>
    </div>
  </div></div>;

  if (stage === 'review' && scoring) return <div style={styles.container}><Header title={`${opMeta.label}: Review`} /><div style={styles.body}>
    <div style={styles.card}>
      <h2 style={{ color: COLORS.navyBlue, marginTop: 0 }}>Result summary</h2>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(110px,1fr))', gap: '9px', marginBottom: '18px' }}>
        {[
          ['Total items', scoring.total_items], ['Attempted', scoring.attempted_count], ['Correct', scoring.correct_count], ['Attempted incorrect', scoring.incorrect_count], ['Unattempted', scoring.unattempted_count],
        ].map(([label, value]) => <div key={label} style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.borderGray}`, borderRadius: '7px', padding: '10px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: COLORS.textGray }}>{label}</div><div style={{ fontSize: '22px', fontWeight: '800', color: COLORS.navyBlue }}>{value}</div></div>)}
      </div>
      <div style={{ marginBottom: '14px', fontSize: '14px' }}><strong>Band:</strong> {scoring.band?.descriptor || 'Not available'}</div>
      {scoring.band?.out_of_range && <div style={styles.errorBox}>{scoring.band.reason}</div>}
      <label style={styles.label}>Notes (optional)</label><textarea style={{ ...styles.input, minHeight: '72px', fontFamily: 'inherit' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div style={{ marginTop: '16px' }}><button style={{ ...styles.button, display: 'inline-flex', alignItems: 'center', gap: '7px' }} onClick={submitAndLock} disabled={loading}><Save size={17} /> {loading ? 'Saving…' : 'Save'}</button></div>
    </div>
  </div></div>;

  if (stage === 'complete') return <div style={styles.container}><Header title="Numeracy: Saved" /><div style={styles.body}><div style={styles.card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', color: COLORS.green, marginBottom: '12px' }}><CheckCircle size={26} /><h2 style={{ fontSize: '20px', margin: 0 }}>Saved</h2></div>
    {allOperationsComplete ? (
      <>
        <p style={{ color: COLORS.navyBlue, fontWeight: '700' }}>All four numeracy operations are complete.</p>
        <button style={styles.button} onClick={onBack}>Return to student screening dashboard</button>
      </>
    ) : (
      <>
        <button style={styles.button} onClick={() => setStage('setup')}>Enter another operation</button>
        <button style={{ ...styles.button, marginLeft: '10px', backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `1px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
      </>
    )}
  </div></div></div>;

  return null;
}
